// Copyright (c) 2021 WiseTime. All rights reserved.

import styled, { css, SimpleInterpolation } from "styled-components/macro"
import { Link, NavLink } from "react-router-dom"
import {
  IS_DYNAMIC_SHADOW_CLASSNAME,
  SUGGESTED_TAG_OVERLAY_CLASSNAME,
} from "features/shared/classNames"
import { darken, lighten } from "polished"
import SvgIcon from "./SvgIcon"
import { HexColor } from "../timeline/color"
import { isIosWiseTime } from "../../app/config"
import { ThemeType } from "../../styled/styles"

export const TextEllipsis = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

export const DialogWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 107;
  background-color: rgba(0, 0, 0, .6);
`

export const Paper = css`
  background-color: ${({ theme }): string => theme.colors.white};
  border-radius: 2px;
  border: 1px solid ${({ theme }): string => theme.colors.seashell};
  box-shadow: ${({ theme }) => theme.paperShadow};
`

export const Tag = css`
  display: inline-block;
  vertical-align: top;
  font-size: 1.1rem;
  font-weight: 600;
  max-width: 150px;
  height: 22px;
  line-height: 21px;
  position: relative;
  padding: 0 .6rem;
  border-radius: 2px;
  color: ${({ theme }): string => theme.colors.indigo};
  border: 1px solid ${({ theme }): string => theme.colors.perano};
  background-color: ${({ theme }): string => theme.colors.lavender};
  transition: all .3s ease;
`

export const TagSuggested = css`
  ${Tag};
  border-style: dashed;
  background-color: ${({ theme }): string => theme.colors.white};
  cursor: default;
`

export const TagRemoveButton = styled.button`
  display: inline-block;
  vertical-align: top;
  width: 21px;
  height: 100%;
  cursor: pointer;
  position: relative;
  color: inherit;
  border-left: 1px solid ${({ theme }): string => theme.colors.perano};
  margin-right: -.7rem;
  margin-left: 7px;
  transition: opacity .3s;

  &:after,
  &:before {
    content: "";
    width: 10px;
    height: 0;
    border-width: 1px;
    border-style: solid;
    position: absolute;
    top: 50%;
    left: 5px;
    margin-top: -1px;
  }

  &:before {
    transform: rotate(-45deg);
  }

  &:after {
    transform: rotate(45deg);
  }

  &:hover {
    opacity: .5;
  }
`

export const NoDescriptionText = styled.span`
  font-weight: 400;
  font-style: italic;
`

export const BreakWord = css`
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
`

export const TagActivity = css`
  ${Tag};
  color: ${({ theme }): string => theme.colors.charcoal};
  border-color: ${({ theme }): string => theme.colors.gallery};
  background-color: ${({ theme }): string => theme.colors.seashell};
  border-radius: 11px;
`

export const SharedTimeRow = styled.li`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-left: 9px;
  border-style: solid;
`

export const SharedResultItem = css<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  transition: background-color .3s;
  
  &:hover {
    background-color: ${({ theme }): string => darken(0.04, theme.colors.white)};
  }
  
  ${({ isActive, theme }): SimpleInterpolation => isActive && css`
    background-color: ${darken(0.04, theme.colors.white)};
  `};
`

export const AbbreviatedTeamName = styled.span`
  display: inline-block;
  vertical-align: middle;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 1rem;
  font-weight: 700;
  line-height: 18px;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  text-transform: uppercase;
  text-align: center;
  background-color: ${({ theme }): string => theme.colors.mortar};
  border: 1px solid ${({ theme }): string => theme.colors.mortar};
  margin-right: 0.7rem;
  @media (max-width: ${({ theme }): string => theme.screen.sm }) {
    vertical-align: 0;
  }
`

export const ContentMessageWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  padding: 1rem;
`

export const ContentMessage = styled.h2`
  color: ${({ theme }): string => theme.colors.dustyGray};
  font-weight: 600;
`

export const ContentSubMessage = styled.span`
  display: block;
  color: ${({ theme }): string => theme.colors.dustyGray};
  font-size: 1.4rem;
  font-weight: 200;
  line-height: 28px;
`

// Page styles start
export const Page = styled.div`
  position: relative;
  padding-top: ${({ theme }): string => theme.headerHeight};
  min-height: 100vh;
  min-height: 100dvh;
  
  @media (min-width: ${({ theme }): string => theme.screen.md}) {
    padding-left: ${({ theme }): string => theme.wtSidebarWidth};
  }
  
  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    background-color: ${({ theme }): string => theme.colors.white};
  }
  
  @media (max-width: ${({ theme }): string => theme.screen.sm }) {
    padding-top: 136px;
  }
`

export const PageHeader = styled.header`
  position: fixed;
  top: 0;
  right: 0;
  left: ${({ theme }): string => theme.wtSidebarWidth};
  z-index: 103;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gainsboro};
  transition: transform .5s;

  &${`.${IS_DYNAMIC_SHADOW_CLASSNAME}`} {
    &:not(.is-scrolled) {
      border-color: transparent;
      box-shadow: none;
    }
  }

  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    left: 0;
  }
`

export const PageHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }): string => theme.colors.white};
  padding: .8rem 1rem .8rem 1.6rem;

  @media (min-width: ${({ theme }): string => theme.screen.md }) {
    padding-left: 3.4rem;
    padding-right: 1rem;
  }

  @media (max-width: ${({ theme }): string => theme.screen.sm }) {
    flex-wrap: wrap;
  }
`

export const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    flex-direction: row;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    justify-content: center;
    height: calc(100vh - 94px);
    height: calc(100dvh - 94px);
  }
`

export const ActionPaneWrapper = styled.div`
  padding: 1.8rem 1.6rem 2rem;

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    width: ${({ theme }): string => theme.groupColumnWidht};
    min-width: ${({ theme }): string => theme.groupColumnWidht};
    border-left: 1px solid ${({ theme }): string => theme.colors.gallery};
    padding-bottom: 0;
    overflow-y: auto;
  }
`

export const ReviewPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    width: calc(100% - ${({ theme }): string => theme.groupColumnWidht});
  }

  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    order: 2;
  }
`

export const TimeBlockListWrapper = styled.div`
  position: relative;
  padding: 2rem 3.4rem 0;

  @media (min-width: ${({ theme }): string => theme.screen.md}) {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
  }

  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    padding: 0 1.6rem;
  }

  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    padding-bottom: 7.6rem;
  }
`

export const TimeBlockRowsWrapper = styled.ul`
  list-style: none;
  margin: 0;
  
  &:hover {
    .${SUGGESTED_TAG_OVERLAY_CLASSNAME} {
      width: 38px;
      border-left-width: 1px;
    }
  }
`

export const TimeBlockWrapper = styled.div`
  padding-bottom: 2.8rem;
  &:last-child {
    padding-bottom: 5.2rem;
  }
`

export const TimeBlockHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`

export const BlockDuration = styled.div`
  display: flex;
  align-items: center;
`

export const TimeBlockCheckBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }): string => theme.colors.dimGrey};
  padding-right: 1.5rem;
  flex: 1;

  @media (min-width: ${({ theme }): string => theme.screen.xs}) {
    font-size: 1.4rem;
  }
`

export const TimeBlockCheckBoxLabel = styled.span`
   @media (min-width: ${({ theme }): string => theme.screen.lg}) {
    font-size: 1.3rem;
  }
`

export const ActionPaneButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 0 2.2rem 1rem;
  margin-top: 2rem;

  > * {
    margin-bottom: 1rem;

    &:not(:last-child) {
      margin-right: 1rem;
    }
  }
`

export const ActionPaneButton = styled.button`
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 3px;
`

export const PrimaryButton = styled(ActionPaneButton)`
  color: ${({ theme }): string => theme.colors.white};
  background-color: ${({ theme }): string => theme.colors.indigo};
  border: 1px solid ${({ theme }): string => theme.colors.indigo};
  transition: background-color .3s ease;

  &:hover {
    background-color: ${({ theme }): string => darken(0.15, theme.colors.indigo)};
    border-color: ${({ theme }): string => darken(0.15, theme.colors.indigo)};
  }
`

export const PrimaryRevertedButton = styled(ActionPaneButton)`
  position: relative;
  color: ${({ theme }): string => theme.colors.aqxDark};
  background-color: ${({ theme }): string => theme.colors.gallery};
  border: 1px solid ${({ theme }): string => theme.colors.gallery};
  transition: background-color .3s ease;

  &:hover {
    background-color: ${({ theme }) => darken(0.03, theme.colors.lavender)};
  }
`

export const NegativeButton = styled(ActionPaneButton)`
  background-color: ${({ theme }): string => theme.colors.fireBrick};
  color: ${({ theme }): string => theme.colors.white};

  &:hover {
    background-color: ${({ theme }): string => theme.colors.cinnabar};
  }
`

export const SecondaryButton = styled(ActionPaneButton)`
  background-color: ${({ theme }): string => theme.colors.white};
  border: 1px solid ${({ theme }): string => theme.colors.gallery};
  transition: background-color .3s ease;

  &:hover {
    background-color: ${({ theme }): string => theme.colors.gallery};
  }
`

export const IconButtonStyles = css`
  width: 2.2rem;
  height: 2.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`

export const SecondaryFilledButton = styled(ActionPaneButton)<{ isRow?: boolean }>`
  background-color: ${({ theme }): string => theme.colors.gallery};
  border: 1px solid ${({ theme }): string => theme.colors.gallery};
  transition: all .3s ease;

  &:hover {
    border-color: ${({ theme }): string => darken(0.05, theme.colors.gallery)};
    background-color: ${({ theme }): string => darken(0.05, theme.colors.gallery)};
  }
  
  ${({ isRow }) => isRow && IconButtonStyles};
`

export const DateTabsWrapper = styled.ul`
  flex: 1;
  display: flex;
  justify-content: space-between;
  border-left: 1px solid ${({ theme }): string => theme.colors.silverSand};
  border-right: 1px solid ${({ theme }): string => theme.colors.silverSand};
  list-style: none;
  margin: 0;
`

export const DateLinkWrapper = styled.li<{ isActive?: boolean }>`
  flex: 1;

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    &:not(:last-child) {
      border-right: 1px solid ${({ theme }): string => theme.colors.silverSand};
    }
  }

  ${({ isActive, theme }): SimpleInterpolation => !isActive && css`
    @media (max-width: ${theme.screen.sm}) {
      display: none;
    }
  `}
`

export const DateLink = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 1.1rem;
  color: ${({ theme }): string => theme.colors.dimGray};
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  height: ${isIosWiseTime ? "43px" : "44px"};
  padding: 0 .5rem;
  transition: background-color .3s ease;
  
  &.active {
    background-color: ${({ theme }): string =>theme.colors.white};
    position: relative;
    &:after {
      content: "";
      width: 100%;
      height: 1px;
      background-color: ${({ theme }): string =>theme.colors.white};
      position: absolute;
      left: 0;
      bottom: -1px;
    }
  }

  &:hover {
    background-color: ${({ theme }): string => theme.colors.zircon};
  }
  
  &.is-today {
    text-decoration: underline;
  }
  
  &.has-error {
    color: ${({ theme }): string => theme.colors.mexicanRed};

    &:not(.active):not(:hover) {
      background-color: ${({ theme }): string => theme.colors.mistyRose};
    }
  }

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    justify-content: space-between;
  }

  @media (min-width: ${({ theme }): string => theme.screen.md}) {
    padding: 0 .8rem;
  }

  @media (min-width: ${({ theme }): string => theme.screen.lg}) {
    padding: 0 1.5rem;
  }

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    white-space: nowrap;
  }

  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    padding: .7rem 0 2rem;
  }

  @media (max-width: ${({ theme }): string => theme.screen.md}) and (min-width: ${({ theme }): string => theme.screen.sm}) {
    font-size: .9rem;
  }
`

export const DateArrowLink = styled(Link)`
  width: 4.3rem;
  display: flex;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  transition: background-color .3s ease;

  &:hover {
    background-color: ${({ theme }): string => theme.colors.white};
  }

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    width: 4rem;
  }
`

export const DateLabel = styled.span`
  text-align: left;
`

export const IconArrowShared = styled.span`
  width: 22px;
  height: 22px;
  position: relative;

  &:before,
  &:after {
    content: '';
    width: 8px;
    height: 2px;
    background-color: ${({ theme }): string => theme.colors.grey};
    position: absolute;
  }
`

export const IconArrowLeft = styled(IconArrowShared)`
  &:before,
  &:after {
    left: 3px;
  }

  &:before {
    top: 10px;
    transform: rotate(45deg) translate(4px, 0);
  }

  &:after {
    top: 11px;
    transform: rotate(-45deg) translate(4px, 0);
  }
`

export const IconArrowRight = styled(IconArrowShared)`
  &:before,
  &:after {
    left: 11px;
  }

  &:before {
    top: 11px;
    transform: rotate(45deg) translate(-4px, 0);
  }

  &:after {
    top: 10px;
    transform: rotate(-45deg) translate(-4px, 0);
  }
`

export const SortDropdownWrapper = styled.div<{
  isRecentList: boolean,
}>`
@media (max-width: ${({ theme }): string => theme.screen.xxs}) {
    ${({ isRecentList }): SimpleInterpolation => isRecentList && css`
      display: none;
    `}
}`

export const PaneInfoSelected = styled.div`
  display: flex;

  @media(max-width: ${({ theme }): string => theme.screen.xxs}) {
    flex-direction: column;
  }
`

export const SelectedRowsLabel = styled.span`
  display: inline-block;
  color: inherit;
  font-size: 1.2rem;
  font-weight: 600;
  line-height: 27px;
  margin: 3px 0 0 5px;
`

export const SelectedRowsLabelTrash = styled.span`
  color: ${({ theme }): string => theme.colors.charcoal};
  font-size: 1.5rem;
  font-weight: 700;
  display: inline-block;
  vertical-align: top;
  line-height: 30px;
  margin: 0 5px 3px 0;
`

export const Loader = styled.div`
  display: inline-block;
  border: 3px solid transparent;
  border-top: 3px solid ${({ theme }): string => theme.colors.silver};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  margin: 4px 0 0 11px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

export const ActionPaneHeader = styled.div`
  position: relative;
  padding: 2rem 2.2rem;

  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
`

export const ActionPaneDetails = styled.div`
  display: flex;
  justify-content: space-between;
`

export const ActionPaneInfo = styled.div`
  display: flex;
  flex-direction: column;
`

const ButtonOutlined = css`
  font-size: 14px;
  padding: 8px 20px;
  border-radius: 3px;
`

export const BackToTimelineLink = styled(Link)`
  ${ButtonOutlined};
  border: 1px solid ${({ theme }): string => theme.colors.silver};
  background: ${({ theme }): string => theme.colors.white};
  transition: background-color .3s ease;

  &:hover {
    background-color: ${({ theme }): string => theme.colors.gallery};
  }
  
  @media (max-width: ${({ theme }): string => theme.screen.xxs}) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`

export const ArrowIconThin = styled.span`
  display: inline-block;
  width: 0.9rem;
  height: 0.9rem;
  border-width: 2px 0 0 2px;
  border-style: solid;
  border-color: ${({ theme }): string => theme.colors.dimGray};
`

export const EmptyTrashButton = styled.button`
  ${ButtonOutlined};
  background: ${({ theme }): string => theme.colors.cinnabar};
  color: white;
  transition: background-color .3s ease;

  &:hover {
    background-color: ${({ theme }): string => theme.colors.fireBrick};
  }
`

export const KeyWord = styled.span`
  font-weight: 600;
`

export const SiteNameLabel = styled(KeyWord)`
  ${BreakWord};
`

export const SharedDropdownContent = css`
  border-radius: 3px;
  background-color: ${({ theme }): string => theme.colors.white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, .15);
`

export const ListItemLabel = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }): string => theme.colors.charcoal};
  font-weight: 500;
`

export const ListItemDescription = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
  font-weight: 400;
  font-size: 1rem;
  color: ${({ theme }): string => theme.colors.charcoal};
`

export const ActiveListItem = css`
  background-color: ${({ theme }): string => theme.colors.selago};
  
  ${ListItemLabel} {
    color: ${({ theme }): string => theme.colors.indigo};
  }
  
  ${ListItemDescription} {
    color: ${({ theme }): string => theme.colors.manatee};
  }
`

export const inProgressStyles = (inProgress: boolean, theme: ThemeType) => inProgress && css`
  border-color: ${({ theme }) => theme.colors.indigo};
  background-color: ${({ theme }) => lighten(0.37, theme.colors.indigo)};
  --border-size: 2px;
  --border-angle: 0turn;
  background-image: conic-gradient(
    from var(--border-angle),
    #f2f1fd,
    #f2f1fd 50%,
    #f2f1fd
  ),
  conic-gradient(
    from var(--border-angle),
    transparent 20%,
    ${theme.colors.melrose},
    ${theme.colors.indigo}
  );
  background-size: calc(100% - (var(--border-size) * 2))
    calc(100% - (var(--border-size) * 2)),
    cover;
  background-position: center center;
  background-repeat: no-repeat;
  animation: bg-spin 2s linear infinite;
  @keyframes bg-spin {
    to {
      --border-angle: 1turn;
    }
  }
  @property --border-angle {
    syntax: "<angle>";
    inherits: true;
    initial-value: 0turn;
  }
`
export const PrimaryOutlinedButton = styled.button<{ inProgress?: boolean }>`
  cursor: pointer;
  display: inline-block;
  text-align: left;
  vertical-align: top;
  position: relative;
  white-space: nowrap;
  padding: 0 .7rem;
  height: 30px;
  line-height: 28px;
  border: 1px solid ${({ theme }): string => theme.colors.indigo};
  border-radius: 2px;
  transition: all .3s ease;
  color: ${({ theme }): string => theme.colors.indigo};
  background-color: ${({ theme }): string => theme.colors.white};

  ${({ theme, inProgress }) => {
    if (inProgress) {
      return css`
        pointer-events: none;
        ${inProgressStyles(true, theme)};
      `
    }
    return css`
      &:hover {
          color: ${({ theme }): string => theme.colors.white};
          background-color: ${({ theme }): string => theme.colors.indigo};
      }
    `
  }};
`

export const PrimaryOutlinedButtonWithShortcut = styled(PrimaryOutlinedButton)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
`

const SharedShortcut = styled.span`
  display: inline-block;
  text-align: center;
  min-width: 16px;
  height: 16px;
  line-height: 14px;
  border-width: 1px;
  border-style: solid;
  border-radius: 3px;
  margin-left: 5px;
`

export const PrimaryOutlinedButtonShortcut = styled(SharedShortcut)`
  border-color: ${({ theme }) => lighten(0.3, theme.colors.indigo)};
`

export const PrimaryRevertedButtonShortcut = styled(SharedShortcut)`
  border-color: ${({ theme }) => lighten(0.3, theme.colors.aqxDark)};
`

export const IconButton = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 3px;
  transition: all .3s ease;
`

export const IconButtonActive = css`
  color: ${({ theme }) => theme.colors.indigo};
  background-color: ${({ theme }) => theme.colors.lavender};
`

export const WarningIconWrapper = styled.span`
  color: ${({ theme }) => theme.colors.flamenco};
  border-radius: 50%;
  background-color: ${({ theme }) => lighten(0.4, theme.colors.flamenco)};
`

export const TagOverlayButton = styled.a<{ tagColor: HexColor, isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1px 2px 2px;
  width: 17px;
  height: 16px;
  line-height: 17px;
  margin-left: 1px;
  transition: all .3s ease;
  
  ${({ theme, tagColor, isDisabled }) => {
    if (!isDisabled) {
      return css`
        color: ${darken(0.5, tagColor)};
        background-color: ${tagColor};
      `
    }
    return css`
      color: ${theme.colors.dimGray};
      background-color: ${theme.colors.white};
    `
  }};

  &:hover {
    background-color: ${({ isDisabled, theme, tagColor }) => isDisabled
    ? theme.colors.gallery
    : darken(0.1, tagColor)};
  }
`

export const TagOverlaySvgIcon = styled(SvgIcon)`
  width: 13px;
  min-width: 13px;
  height: 14px;
`

export const ErrorMessage = styled.p`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.dangerColor};
  line-height: normal;
  margin: 5px 0 0 0;
`

export const LinkUnderlineSharedStyles = css`
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`

export const LinkUnderline = styled.a`
  ${LinkUnderlineSharedStyles};
`
